import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

/** Which span of days the calendar is showing. */
export type CalendarMode = "month" | "week" | "weekend";

export const CALENDAR_MODES: { value: CalendarMode; label: string }[] = [
  { value: "month", label: "Month" },
  { value: "week", label: "Week" },
  { value: "weekend", label: "Weekend" },
];

interface CalendarHeaderProps {
  /** The span being shown, already formatted — "September 2026", "Sep 14 – 20, 2026". */
  title: string;
  mode: CalendarMode;
  onModeChange: (mode: CalendarMode) => void;
  onPrev: () => void;
  onNext: () => void;
  onToday: () => void;
  /** True when the span on screen already contains today, which disables "Today". */
  isCurrent: boolean;
}

export function CalendarHeader({
  title,
  mode,
  onModeChange,
  onPrev,
  onNext,
  onToday,
  isCurrent,
}: CalendarHeaderProps) {
  const unit = mode === "month" ? "month" : mode === "week" ? "week" : "weekend";

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
      <h2 className="font-display text-xl text-foreground">{title}</h2>

      <div className="flex items-center gap-2">
        <div className="flex rounded-lg bg-secondary/50 p-0.5">
          {CALENDAR_MODES.map(({ value, label }) => (
            <button
              key={value}
              type="button"
              onClick={() => onModeChange(value)}
              aria-pressed={mode === value}
              className={cn(
                "px-2.5 py-1.5 text-sm font-medium rounded-md transition-colors duration-200",
                "outline-none focus-visible:ring-2 focus-visible:ring-primary/50",
                mode === value
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              {label}
            </button>
          ))}
        </div>

        <button
          type="button"
          onClick={onToday}
          disabled={isCurrent}
          className={cn(
            "px-3 py-1.5 text-sm font-medium rounded-lg transition-colors duration-200",
            "outline-none focus-visible:ring-2 focus-visible:ring-primary/50",
            isCurrent
              ? "text-muted-foreground/40 cursor-not-allowed"
              : "text-primary hover:bg-secondary"
          )}
        >
          Today
        </button>

        <div className="flex rounded-lg border border-border/50 overflow-hidden">
          <button
            type="button"
            onClick={onPrev}
            className="p-2 hover:bg-muted/50 transition-colors outline-none focus-visible:bg-muted/50"
            aria-label={`Previous ${unit}`}
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={onNext}
            className="p-2 hover:bg-muted/50 transition-colors border-l border-border/50 outline-none focus-visible:bg-muted/50"
            aria-label={`Next ${unit}`}
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
